import React from "react";
import Api from "@api";
import { init as contextInit, reducer as contextReducer } from "./state";
import { Dispatch, State } from "./state/types";
import Utils from "../utils";
import { Grid, Box, CircularProgress } from "@material-ui/core";

type Props = {
  draft?: string | null;
};

function localReducer(
  state: {
    loading: boolean;
  },
  action: {
    type: "set-loading";
    value: boolean;
  }
) {
  switch (action.type) {
    case "set-loading":
      return {
        loading: action.value,
      };
  }
}

function localInit() {
  return {
    loading: false,
  };
}

const S = React.createContext<State | null>(null);
const D = React.createContext<Dispatch | null>(null);

export const Context: React.FC<Props> = (props) => {
  const [localState, localDispatch] = React.useReducer(
    localReducer,
    undefined,
    localInit
  );
  const [contextState, contextDispatch] = React.useReducer(
    contextReducer,
    undefined,
    contextInit
  );

  React.useEffect(() => {
    if (props.draft) {
      localDispatch({
        type: "set-loading",
        value: true,
      });
      Api.drafts.getDrafts().then((drafts) => {
        const draft = drafts.find(({ id }) => id.toString() === props.draft);

        if (draft) {
          Utils.getRequestContextValuesFromDraft(draft).then((values) => {
            localDispatch({
              type: "set-loading",
              value: false,
            });
            contextDispatch({
              type: "reset",
              values,
            });
          });
        }
      });
    }
  }, [props.draft]);

  return localState.loading ? (
    <Grid container spacing={2}>
      <Grid item xs={12} component={Box} height="100%">
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      </Grid>
    </Grid>
  ) : (
    <S.Provider value={contextState}>
      <D.Provider value={contextDispatch}>{props.children}</D.Provider>
    </S.Provider>
  );
};

export function useRequestContext(): [State, Dispatch] {
  const state = React.useContext(S);
  const dispatch = React.useContext(D);

  if (state === null || dispatch === null) {
    throw new Error("useContext must be used within a Context provider");
  }

  return [state, dispatch];
}
